import {
  InlineEditorLogic,
  isEditMode,
  MergeOrUpdateStateAction,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { CheckBoxWithLabelWidget, TabStyles, TextAreaWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { canSubmitLabel } from '~src/catalog/components/dataQuality/canSubmitLabel';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import {
  ContentDetailSection,
  DetailEditButton,
  DetailSaveCancelButtons
} from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailSection';
import { DetailDialogUserDetail } from '~src/spacesPlatform/components/content/contentDetailDialog/DetailDialogUserLink';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { cssClass } from '~src/style';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';
import { UserMapper } from '~src/userManagement/users/models/mappers/UserMapper';


export interface DataCertificationSectionProps {
  readonly isCertified: boolean;
  readonly certifier?: IUserInfo;
  readonly note?: string;
  readonly isActionEnabled: boolean;
  readonly urls: SpacesSiteUrls;
  readonly setCertification: (isCertified: boolean, certificationNote: string) => Promise<IResult>;
  readonly incrementRouterSpaceReactKey: () => void;
}

export interface DataCertificationEditorState {
  isCertified: boolean;
  note: string;
}

const DataCertificationClassname = cssClass('data-certification', 'section', {
  $nest: {
    '&>*': {
      paddingBottom: TabStyles.Spacing.DefaultSpace,
    }
  }
});


export function DataCertificationSection(props: DataCertificationSectionProps): JSX.Element {

  const onSave = React.useCallback(
    async (editorState: DataCertificationEditorState) => {
      await props.setCertification(editorState.isCertified, editorState.note)
        .then(response => {
          if (response.errors && response.errors.length) { // unexpected server error
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.datasourceCertification_failed() });
          }
          return response;
        })
        .then(rejectIfResultContainsError)
        .then(props.incrementRouterSpaceReactKey);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.setCertification,
      props.incrementRouterSpaceReactKey,
    ]
  );

  /**
   * Data Quality Service will throw if we send an enabled certification with a blank note
   */
  const canSave = (editorState: DataCertificationEditorState) => {
    const command = canSubmitLabel({ message: editorState.note, active: editorState.isCertified });

    return (command === 'SUBMIT' || command === 'DELETE') && !_.isEqual(editorState, initialState);
  };

  const initialState: DataCertificationEditorState = {
    isCertified: props.isCertified,
    note: props.note || '',
  };

  const logic: InlineEditorLogic<DataCertificationEditorState> = useInlineEditor<DataCertificationEditorState>({
    editorStateWhenEnteringEditMode: initialState,
    canEdit: props.isActionEnabled,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header={Messages.dataSourceCertification_certificationLabel()}
        testId={'certification'}
        detail={
          <DataCertificationEditMode
            previouslyCertified={props.isCertified}
            previousCertifier={props.certifier}
            isCertified={logic.editorState.isCertified}
            note={logic.editorState.note}
            urls={props.urls}
            setEditorState={logic.setEditorStateWithinEditMode}
            disabled={logic.isActionBusy}
          />
        }
        isActionAllowed={true}
        action={
          <DetailSaveCancelButtons
            inlineEditorLogic={logic}
          />
        }
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header={Messages.dataSourceCertification_certificationLabel()}
        testId={'certification'}
        detail={
          <DataCertificationReadingMode
            isCertified={props.isCertified}
            certifier={props.certifier}
            note={props.note}
            urls={props.urls}
          />
        }
        isActionAllowed={logic.canEdit}
        action={
          <DetailEditButton
            buttonRef={logic.editButtonRef}
            buttonLabel={Messages.dataSourceCertification_editCertification()}
            buttonTestId={'edit-certification'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}


export interface DataCertificationReadingModeProps {
  readonly isCertified: boolean;
  readonly certifier?: IUserInfo;
  readonly note: string;
  readonly urls: SpacesSiteUrls;
}

export function DataCertificationReadingMode(props: DataCertificationReadingModeProps): JSX.Element {
  return (
    <div className={DataCertificationClassname}>
      {props.isCertified ?
        <CertifiedByComponent
          certifier={props.certifier}
          urls={props.urls}
        /> :
        <div>{Messages.dataSourceCertification_uncertified()}</div>}
      {!_.isEmpty(props.note) &&
        <MarkupField markup={props.note} testId="certification-note-Markup" />}
    </div>
  );
}

export interface DataCertificationEditModeProps {
  readonly previouslyCertified: boolean;
  readonly previousCertifier: IUserInfo;
  readonly isCertified: boolean;
  readonly note: string;
  readonly urls: SpacesSiteUrls;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<DataCertificationEditorState>>;
  readonly disabled: boolean;
}

export function DataCertificationEditMode(props: DataCertificationEditModeProps): JSX.Element {
  const TextAreaStyle: React.CSSProperties = {
    width: '100%',
    resize: 'vertical',
    display: 'block'
  };

  return (
    <div className={DataCertificationClassname}>
      <CheckBoxWithLabelWidget
        checked={props.isCertified}
        label={Messages.dataSourceCertification_editCertificationCheckboxLabel()}
        testId={'certify-datasource'}
        removeLeftMargin={true}
        handleChange={value => props.setEditorState({ isCertified: value })}
        disabled={props.disabled}
      />
      {props.previouslyCertified && props.isCertified ?
        <CertifiedByComponent
          certifier={props.previousCertifier}
          urls={props.urls}
        /> : ''}
      <div>{Messages.dataSourceCertification_certificationDetailedDescription()}</div>
      <TextAreaWidget
        label={Messages.dataSourceCertification_noteLabel()}
        testId={'certification-note'}
        value={props.note}
        onChange={e => props.setEditorState({ note: e.target.value })}
        style={TextAreaStyle}
        className={style({ width: '100%' })}
        rows={6}
        disabled={props.disabled}
      />
    </div>
  );
}


export interface CertifiedByComponentProps {
  readonly certifier: IUserInfo;
  readonly urls: SpacesSiteUrls;
}

export function CertifiedByComponent(props: CertifiedByComponentProps): JSX.Element {
  // certifier can be empty if in a hidden user scenario. this fills in default values
  const user = UserMapper.mapOne(props.certifier);

  return (
    <div>
      <span style={{ paddingRight: TabStyles.Spacing.SmallSpace }}>
        {Messages.datasourceHeaderCertifier()}
      </span>
      <DetailDialogUserDetail
        user={user}
        urls={props.urls}
      />
    </div>
  );
}
